/**
 * SupportCodes.tsx — attended one-time support code (SPEC §6).
 *
 * POST /support-codes returns { code, expires_at }. The code is ≥64-bit CSPRNG
 * rendered as a short human string — NOT a numeric PIN. The end user runs the
 * bootstrapper from GET /support-codes/{code}. We display the code + a live TTL.
 */

import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useApi } from '@/api/provider';
import type { SupportCode } from '@/types';

export default function SupportCodes() {
  const api = useApi();
  const [code, setCode] = useState<SupportCode | null>(null);
  const [remaining, setRemaining] = useState(0);

  const mutation = useMutation({
    mutationFn: () => api.createSupportCode(),
    onSuccess: (c) => setCode(c),
  });

  useEffect(() => {
    if (!code) return;
    const tick = () => {
      const ms = new Date(code.expires_at).getTime() - Date.now();
      setRemaining(Math.max(0, Math.floor(ms / 1000)));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [code]);

  // Shareable redeem path: existing GET /support-codes/{code} bootstrapper route.
  const joinUrl = code ? `${window.location.origin}/support-codes/${code.code}` : '';
  const expired = remaining <= 0;
  const ttl = `${Math.floor(remaining / 60)}:${String(remaining % 60).padStart(2, '0')}`;

  return (
    <div className="p-6 sm:p-8">
      {/* ---- Page header ---- */}
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-helm-text">Support</h1>
          <p className="mt-0.5 text-sm text-helm-muted">
            Generate a one-time code so an end user can start an attended support session.
          </p>
        </div>
        <button
          type="button"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
          className="rh-btn-primary"
        >
          {mutation.isPending ? (
            'Generating…'
          ) : (
            <>
              <IconPlus />
              {code ? 'New code' : 'Generate code'}
            </>
          )}
        </button>
      </div>

      {mutation.isError && (
        <div className="mb-5 rounded-lg border border-helm-bad/30 bg-helm-bad/10 px-4 py-3 text-sm text-helm-bad">
          Failed to generate a code. Please try again.
        </div>
      )}

      {!code ? (
        /* ---- Empty state ---- */
        <div className="rh-card max-w-2xl p-8 text-center">
          <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-helm-accent/10 text-helm-accent">
            <IconLifebuoy />
          </div>
          <h2 className="mt-4 text-lg font-semibold text-helm-text">
            Start an attended support session
          </h2>
          <p className="mx-auto mt-2 max-w-md text-sm text-helm-muted">
            Attended support lets you help someone who is sitting at their computer right
            now — no pre-installed agent required. Generate a single-use code, share it with
            the end user, and they run a one-time helper to bring their machine online under
            Access. Codes expire in ≤10 minutes and are rate-limited on redemption.
          </p>
          <button
            type="button"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
            className="rh-btn-primary mx-auto mt-6"
          >
            {mutation.isPending ? (
              'Generating…'
            ) : (
              <>
                <IconPlus />
                Generate code
              </>
            )}
          </button>
        </div>
      ) : (
        /* ---- Active code: two-column layout ---- */
        <div className="grid max-w-4xl gap-5 lg:grid-cols-2">
          {/* (1) The code + shareable join URL */}
          <div className="rh-card p-6">
            <div className="flex items-center justify-between gap-4">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-helm-muted">
                Support code
              </div>
              <span
                className={`rh-badge ${
                  expired
                    ? 'bg-helm-elevated text-helm-muted'
                    : remaining < 60
                      ? 'bg-helm-bad/15 text-helm-bad'
                      : 'bg-helm-good/15 text-helm-good'
                }`}
                title="Time until this code expires"
              >
                <span
                  className={`h-1.5 w-1.5 rounded-full ${
                    expired ? 'bg-helm-muted' : remaining < 60 ? 'bg-helm-bad' : 'bg-helm-good'
                  }`}
                />
                {expired ? 'Expired' : ttl}
              </span>
            </div>

            <div
              className={`mt-3 select-all break-all font-mono text-4xl font-bold tracking-widest ${
                expired ? 'text-helm-muted line-through' : 'text-helm-text'
              }`}
            >
              {code.code}
            </div>

            <button
              type="button"
              onClick={() => navigator.clipboard.writeText(code.code)}
              className="rh-btn-ghost mt-3"
              title="Copy the code to your clipboard"
            >
              <IconCopy />
              Copy code
            </button>

            <div className="mt-6">
              <label className="text-[11px] font-semibold uppercase tracking-wide text-helm-muted">
                Shareable join link
              </label>
              <div className="mt-1.5 flex items-center gap-2">
                <input
                  type="text"
                  readOnly
                  value={joinUrl}
                  onFocus={(e) => e.currentTarget.select()}
                  className="rh-input min-w-0 flex-1 select-all font-mono text-xs"
                  title="Send this link to the end user"
                />
                <button
                  type="button"
                  onClick={() => navigator.clipboard.writeText(joinUrl)}
                  className="rh-btn-ghost shrink-0"
                  title="Copy the join link to your clipboard"
                >
                  <IconCopy />
                  Copy
                </button>
              </div>
              <p className="mt-2 text-xs text-helm-muted">
                Single-use · expires in {expired ? '0:00' : ttl}. Generate a new code once it
                is used or expired.
              </p>
            </div>
          </div>

          {/* (2) End-user instructions */}
          <div className="rh-card p-6">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-helm-muted">
              Tell the end user
            </div>
            <ol className="mt-4 space-y-4">
              <Step n={1} title="Open the link">
                In their web browser, have them visit{' '}
                <span className="font-mono text-helm-text">{joinUrl}</span>.
              </Step>
              <Step n={2} title="Enter the code">
                If prompted, they type the support code{' '}
                <span className="font-mono font-semibold text-helm-text">{code.code}</span>{' '}
                exactly as shown.
              </Step>
              <Step n={3} title="Run the downloaded helper">
                A small one-time helper downloads. They open it and approve the prompt to
                allow the session.
              </Step>
              <Step n={4} title="You'll appear under Access" last>
                Their machine connects and shows up in your{' '}
                <span className="text-helm-text">Access</span> list — start a Control or View
                session from there.
              </Step>
            </ol>
          </div>
        </div>
      )}
    </div>
  );
}

/* --- step row --- */
function Step({
  n,
  title,
  children,
  last = false,
}: {
  n: number;
  title: string;
  children: React.ReactNode;
  last?: boolean;
}) {
  return (
    <li className="relative flex gap-3.5">
      {!last && (
        <span
          className="absolute left-[15px] top-8 bottom-[-1rem] w-px bg-helm-border"
          aria-hidden="true"
        />
      )}
      <span className="z-10 grid h-8 w-8 shrink-0 place-items-center rounded-full bg-helm-accent/15 text-sm font-bold text-helm-accent">
        {n}
      </span>
      <div className="pt-0.5">
        <div className="text-sm font-semibold text-helm-text">{title}</div>
        <p className="mt-0.5 text-sm text-helm-muted">{children}</p>
      </div>
    </li>
  );
}

/* --- inline stroke icons (currentColor) --- */
function IconPlus() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

function IconCopy() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="9" y="9" width="11" height="11" rx="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}

function IconLifebuoy() {
  return (
    <svg
      width="28"
      height="28"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="3.5" />
      <path d="m5.6 5.6 3.9 3.9M14.5 14.5l3.9 3.9M18.4 5.6l-3.9 3.9M9.5 14.5l-3.9 3.9" />
    </svg>
  );
}
